import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import DashboardLayout from '../layout/DashboardLayout';
import { fetchLeads } from '../../store/slices/leadsSlice';
import Spinner from '../common/Spinner';
import styles from './MapView.module.css';
import DashboardHeader from '../dashboard/DashboardHeader';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaHome, FaSearch, FaChevronLeft, FaChevronRight, FaFilter } from 'react-icons/fa';
import { format } from 'date-fns';
import { config } from '../../config/config';

// Set mapbox token
mapboxgl.accessToken = config.mapboxAccessToken;

const MapComponent = ({ mapLeads, handleLeadClick, selectedLead }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef({});
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/streets-v11');

  // Available map styles
  const mapStyles = [
    { id: 'mapbox://styles/mapbox/streets-v11', name: 'Streets' },
    { id: 'mapbox://styles/mapbox/satellite-streets-v11', name: 'Satellite' },
    { id: 'mapbox://styles/mapbox/light-v10', name: 'Light' },
    { id: 'mapbox://styles/mapbox/dark-v10', name: 'Dark' },
    { id: 'mapbox://styles/mapbox/outdoors-v11', name: 'Outdoors' },
    { id: 'mapbox://styles/mapbox/navigation-day-v1', name: 'Navigation' }
  ];

  // Create custom home icon SVG
  const createHomeIcon = (color) => {
    return `data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}"><path d="M12 2L2 9.5V22h20V9.5L12 2zm7 18H5v-9l7-5.5 7 5.5v9z"/><path d="M9 14h6v8H9z"/></svg>`;
  };

  // Handle style change
  const handleStyleChange = (styleId) => {
    setMapStyle(styleId);
    if (map.current) {
      map.current.setStyle(styleId);
    }
  };

  useEffect(() => {
    if (!map.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: mapStyle,
        center: [-98.5795, 39.8283], // Default to US center
        zoom: 3.5, // Lower initial zoom level
        pitchWithRotate: true,
        dragRotate: true
      });

      // Add controls
      map.current.addControl(new mapboxgl.NavigationControl());
      map.current.addControl(new mapboxgl.FullscreenControl());
      map.current.addControl(new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true,
        showUserHeading: true
      }));
      
      // Add scale control
      map.current.addControl(new mapboxgl.ScaleControl({
        maxWidth: 80,
        unit: 'imperial'
      }));

      // Add terrain control if you want 3D terrain
      map.current.on('style.load', () => {
        map.current.addSource('mapbox-dem', {
          'type': 'raster-dem',
          'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
          'tileSize': 512,
          'maxzoom': 14
        });
        map.current.setTerrain({ 'source': 'mapbox-dem', 'exaggeration': 1.5 });
      });
    }

    // Clean up on unmount
    return () => {
      Object.values(markersRef.current).forEach(marker => marker.remove());
      markersRef.current = {};
      
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [mapStyle]);

  // Handle markers
  useEffect(() => {
    if (!map.current || !mapLeads.length) return;

    // Need to wait for style to load before adding markers
    const onStyleLoaded = () => {
      Object.values(markersRef.current).forEach(marker => marker.remove());
      markersRef.current = {};

      // Create bounds object to fit all markers
      const bounds = new mapboxgl.LngLatBounds();

      mapLeads.forEach(lead => {
        // Skip leads without valid coordinates
        if (!lead.coordinates || !Array.isArray(lead.coordinates) || lead.coordinates.length !== 2) {
          console.warn('Lead has invalid coordinates:', lead);
          return;
        }

        // Create custom marker element
        const el = document.createElement('div');
        el.className = 'custom-marker';
        
        // Style for custom marker with home icon
        el.style.width = '40px';
        el.style.height = '40px';
        el.style.backgroundImage = `url('${createHomeIcon(
          lead.priority === 'high' ? '%23ef4444' : 
          lead.priority === 'medium' ? '%23f59e0b' : 
          '%2322c55e'
        )}')`;
        el.style.backgroundSize = 'contain';
        el.style.backgroundRepeat = 'no-repeat';
        el.style.cursor = 'pointer';

        // Create popup with more information
        const popup = new mapboxgl.Popup({ 
          offset: [0, -20],
          closeButton: false,
          maxWidth: '300px'
        })
        .setHTML(`
          <div class="p-3">
            <h3 class="font-medium text-lg mb-2">${lead.siteContact?.name || lead.client?.name}</h3>
            <div class="space-y-1 text-sm">
              <p class="text-gray-600">${lead.formattedAddress || `${lead.siteContact?.address?.street || ''}, ${lead.siteContact?.address?.city || ''}, ${lead.siteContact?.address?.state || ''} ${lead.siteContact?.address?.zipCode || ''}`}</p>
              ${lead.geocodingConfidence < 0.8 ? 
                `<p class="text-amber-600 text-xs">⚠️ Address match confidence: ${Math.round(lead.geocodingConfidence * 100)}%</p>` 
                : ''}
              ${lead.siteContact?.cellPhone ? 
                `<p class="text-gray-600"><i class="fas fa-phone mr-2"></i>${lead.siteContact.cellPhone}</p>` 
                : ''}
              <p class="mt-2">
                <span class="inline-block px-2 py-1 text-xs font-medium rounded-full
                  ${lead.priority === 'high' ? 'bg-red-100 text-red-700' : 
                  lead.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 
                  'bg-green-100 text-green-700'}">
                  ${lead.priority} priority
                </span>
              </p>
            </div>
          </div>
        `);

        // Create and add marker
        const marker = new mapboxgl.Marker({
          element: el,
          anchor: 'bottom'
        })
        .setLngLat([lead.coordinates[1], lead.coordinates[0]])
        .setPopup(popup)
        .addTo(map.current);

        // Extend bounds with this point
        bounds.extend([lead.coordinates[1], lead.coordinates[0]]);

        // Show popup on hover
        el.addEventListener('mouseenter', () => {
          marker.getPopup().addTo(map.current);
        });

        el.addEventListener('mouseleave', () => {
          if (selectedLead?._id !== lead._id) {
            marker.getPopup().remove();
          }
        });

        // Add click handler
        el.addEventListener('click', () => {
          handleLeadClick(lead);
        });

        markersRef.current[lead._id] = marker;
      });

      // Fit map to bounds with padding only if we have valid coordinates
      if (!bounds.isEmpty()) {
        map.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15
        });
      }
    };

    // If style is loaded, add markers immediately; otherwise, wait for the style.load event
    if (map.current.isStyleLoaded()) {
      onStyleLoaded();
    } else {
      map.current.once('style.load', onStyleLoaded);
    }
  }, [mapLeads, handleLeadClick, selectedLead, mapStyle]);

  // Handle selected lead
  useEffect(() => {
    if (!map.current || !selectedLead || !selectedLead.coordinates || !Array.isArray(selectedLead.coordinates) || selectedLead.coordinates.length !== 2) return;

    // Fly to selected location with tilt and rotation
    map.current.flyTo({
      center: [selectedLead.coordinates[1], selectedLead.coordinates[0]],
      zoom: 16,
      pitch: 60,
      bearing: 30,
      duration: 2000,
      essential: true
    });

    // Highlight selected marker
    Object.entries(markersRef.current).forEach(([id, marker]) => {
      const el = marker.getElement();
      if (id === selectedLead._id) {
        el.style.transform = 'scale(1.2) translateY(-10px)';
        el.style.zIndex = 1;
        marker.getPopup().addTo(map.current);
      } else {
        el.style.transform = 'scale(1) translateY(0)';
        el.style.zIndex = 0;
        marker.getPopup().remove();
      }
    });
  }, [selectedLead]);

  return (
    <div className="relative h-full">
      <div className="absolute top-3 right-3 z-10 bg-white rounded-md shadow-md p-2">
        <div className="text-xs font-medium mb-1 text-gray-500">Map Style</div>
        <div className="grid grid-cols-1 gap-1">
          {mapStyles.map(style => (
            <button 
              key={style.id}
              onClick={() => handleStyleChange(style.id)}
              className={`text-xs py-1 px-2 rounded ${
                mapStyle === style.id 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {style.name}
            </button>
          ))}
        </div>
      </div>
      <div ref={mapContainer} className={styles.mapContainer} />
    </div>
  );
};

const LeadCard = ({ lead, isSelected, onClick, error, onViewInMap }) => {
  const dispatch = useDispatch();
  const { leads, loading: apiLoading } = useSelector((state) => state.leads);
  const [geocodingErrors, setGeocodingErrors] = useState({});
  const [geocodingLoading, setGeocodingLoading] = useState(false);

  const getGoogleMapsUrl = (address) => {
    if (!address) return null;
    const query = encodeURIComponent(
      `${address.street}, ${address.city}, ${address.state} ${address.zipCode || ''}`
    );
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };

  // Use site contact address if available, fallback to lead address
  const displayAddress = lead.siteContact?.address || lead.address;
  const hasValidCoordinates = lead.coordinates && Array.isArray(lead.coordinates) && lead.coordinates.length === 2;

  return (
    <div 
      className={`
        bg-white rounded-lg p-3 md:p-4 cursor-pointer
        transition-all duration-200 ease-in-out
        ${isSelected ? 
          'border-l-4 border-blue-500 bg-blue-50 shadow-md' : 
          'border-l-4 border-transparent hover:border-blue-300 hover:bg-gray-50 shadow-sm'
        }
        ${error ? 'border-l-4 !border-red-400' : ''}
      `}
      onClick={onClick}
    >
      <div className="flex justify-between items-start mb-2 md:mb-3">
        <div className="flex items-center space-x-1 md:space-x-2">
          <FaMapMarkerAlt 
            className={`w-4 h-4 md:w-5 md:h-5 transition-colors duration-200 ${
              isSelected ? 'text-blue-500' : 'text-gray-400'
            }`} 
          />
          <span className={`text-sm md:text-base font-medium transition-colors duration-200 ${
            isSelected ? 'text-blue-700' : 'text-gray-700'
          }`}>
            {lead.jobNumber}
          </span>
        </div>
        <span className={`
          px-2 py-1 text-xs font-medium rounded-full
          ${lead.priority === 'high' ? 'bg-red-100 text-red-700' : 
            lead.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 
            'bg-green-100 text-green-700'}
        `}>
          {lead.priority}
        </span>
      </div>

      <div className="space-y-3">
        <div>
          <h3 className={`text-lg font-semibold transition-colors duration-200 ${
            isSelected ? 'text-blue-900' : 'text-gray-800'
          }`}>
            {lead.siteContact?.name || lead.client?.name}
          </h3>
          <p className="text-sm text-gray-500">
            {lead.opportunityType || 'Lead'}
          </p>
        </div>

        <div className="space-y-1">
          <p className={`text-sm ${isSelected ? 'text-blue-800' : 'text-gray-600'}`}>
            {displayAddress?.street}
          </p>
          <p className={`text-sm ${isSelected ? 'text-blue-700' : 'text-gray-600'}`}>
            {displayAddress?.city}, {displayAddress?.state} {displayAddress?.zipCode}
          </p>
          {displayAddress && (
            <div className="flex space-x-3">
              <a
                href={getGoogleMapsUrl(displayAddress)}
                target="_blank"
                rel="noopener noreferrer"
                className={`
                  inline-flex items-center space-x-1 text-xs mt-1
                  ${isSelected ? 'text-blue-600 hover:text-blue-700' : 'text-gray-500 hover:text-gray-700'}
                `}
                onClick={(e) => e.stopPropagation()}
              >
                <FaMapMarkerAlt className="w-3 h-3" />
                <span>View in Maps</span>
              </a>
              <div className="relative group">
                <button
                  className={`
                    inline-flex items-center space-x-1 text-xs mt-1
                    ${hasValidCoordinates 
                      ? (isSelected ? 'text-blue-600 hover:text-blue-700' : 'text-gray-500 hover:text-gray-700')
                      : 'text-gray-400 cursor-not-allowed'
                    }
                  `}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (hasValidCoordinates) {
                      onViewInMap();
                    }
                  }}
                  disabled={!hasValidCoordinates}
                >
                  <FaMapMarkerAlt className="w-3 h-3" />
                  <span>View</span>
                </button>
                {!hasValidCoordinates && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                    No map location available
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-4 text-sm text-gray-500">
          {lead.siteContact?.cellPhone && (
            <div className="flex items-center space-x-1">
              <FaPhone className={isSelected ? 'text-blue-400' : 'text-gray-400'} />
              <span>{lead.siteContact.cellPhone}</span>
            </div>
          )}
          {lead.siteContact?.email && (
            <div className="flex items-center space-x-1">
              <FaEnvelope className={isSelected ? 'text-blue-400' : 'text-gray-400'} />
              <span className="truncate max-w-[150px]">{lead.siteContact.email}</span>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center text-xs pt-2 border-t border-gray-100">
          <div className="flex items-center space-x-1">
            <FaClock className={isSelected ? 'text-blue-400' : 'text-gray-400'} />
            <span className={isSelected ? 'text-blue-600' : 'text-gray-400'}>
              {format(new Date(lead.createdAt), 'MMM d, yyyy')}
            </span>
          </div>
          {error && (
            <span className="text-red-500 font-medium">
              {error}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const MapView = () => {
  const dispatch = useDispatch();
  const { leads, loading: apiLoading, pagination } = useSelector((state) => state.leads);
  const [searchTerm, setSearchTerm] = useState('');
  const [mapLeads, setMapLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [geocodingErrors, setGeocodingErrors] = useState({});
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [priority, setPriority] = useState('all');

  // Load leads once without search term - get a larger batch
  const loadLeads = useCallback(async () => {
    try {
      const params = {
        page: 1,
        limit: 100, // Get a larger batch of leads for client-side filtering
        isArchived: false
      };

      await dispatch(fetchLeads(params)).unwrap();
    } catch (error) {
      console.error('Error loading leads:', error);
    }
  }, [dispatch]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  // Client-side filtering with useMemo
  const handleClientSearch = useCallback((term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page on new search
  }, []);

  // Client-side filtering for leads
  useMemo(() => {
    if (!mapLeads.length) return;

    let filtered = [...mapLeads];

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(lead => {
        const nameMatch = (lead.siteContact?.name || '').toLowerCase().includes(searchLower) ||
                         (lead.client?.name || '').toLowerCase().includes(searchLower);
        const addressMatch = (lead.formattedAddress || '').toLowerCase().includes(searchLower) ||
                           (lead.siteContact?.address?.street || '').toLowerCase().includes(searchLower) ||
                           (lead.siteContact?.address?.city || '').toLowerCase().includes(searchLower) ||
                           (lead.address?.street || '').toLowerCase().includes(searchLower) ||
                           (lead.address?.city || '').toLowerCase().includes(searchLower);
        const jobNumberMatch = (lead.jobNumber || '').toLowerCase().includes(searchLower);
        return nameMatch || addressMatch || jobNumberMatch;
      });
    }

    // Filter by priority
    if (priority !== 'all') {
      filtered = filtered.filter(lead => lead.priority === priority);
    }

    setFilteredLeads(filtered);
  }, [mapLeads, searchTerm, priority]);

  // Pagination for filtered leads
  const paginatedLeads = useMemo(() => {
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;
    return filteredLeads.slice(startIndex, endIndex);
  }, [filteredLeads, currentPage, limit]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(filteredLeads.length / limit);
  }, [filteredLeads, limit]);

  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  // Handle limit change
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing limit
  };

  // Handle priority filter change
  const handlePriorityChange = (e) => {
    setPriority(e.target.value);
    setCurrentPage(1); // Reset to first page when changing priority
  };

  // Update the formatAddress function with improved address formatting
  const formatAddress = (lead) => {
    // Prioritize site contact address
    const address = lead.siteContact?.address || lead.address;
    
    console.log('Lead ID:', lead._id);
    console.log('Site Contact Address:', lead.siteContact?.address);
    console.log('Lead Address:', lead.address);
    console.log('Using Address:', address);
    
    if (!address) return null;

    // Helper function to clean and format address components
    const formatString = (str) => {
      if (!str) return '';
      return str
        .trim()
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .replace(/[^\w\s,.#-]/g, '') // Keep only alphanumeric, spaces, commas, periods, #, and hyphens
        .replace(/\s*,\s*/g, ', '); // Standardize comma spacing
    };

    // Format street address
    let street = formatString(address.street);
    
    // Handle apartment/unit numbers
    if (address.unit) {
      const unit = formatString(address.unit);
      if (unit) {
        // Add unit number with proper formatting
        street = `${street} ${unit.startsWith('#') ? unit : `#${unit}`}`;
      }
    }

    // Format city and state
    const city = formatString(address.city);
    const state = formatString(address.state);
    const zipCode = address.zipCode?.trim();

    // Validate required fields
    if (!street || !city || !state) {
      console.log('Missing required address fields:', { street, city, state });
      return null;
    }

    // Format the address in a standardized way for Mapbox
    let formattedAddress = `${street}, ${city}, ${state}`;
    if (zipCode) {
      formattedAddress += ` ${zipCode}`;
    }

    // Add country for better results
    formattedAddress += ', USA';

    console.log('Final formatted address:', formattedAddress);
    return formattedAddress;
  };

  // Update the geocoding function to use the improved address formatting
  useEffect(() => {
    const getCoordinates = async () => {
      setGeocodingLoading(true);
      const newGeocodingErrors = {};
      
      try {
        const leadsWithCoordinates = await Promise.all(
          leads.map(async (lead) => {
            try {
              const formattedAddress = formatAddress(lead);
              
              if (!formattedAddress) {
                newGeocodingErrors[lead._id] = 'Invalid or incomplete address';
                return {
                  ...lead,
                  coordinates: null,
                  formattedAddress: null,
                  geocodingConfidence: 0
                };
              }

              const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(formattedAddress)}.json?` + 
                new URLSearchParams({
                  access_token: config.mapboxAccessToken,
                  limit: 1,
                  types: 'address',
                  country: 'US',
                  autocomplete: false
                });
              
              const response = await fetch(url);
              
              if (!response.ok) {
                throw new Error('Geocoding request failed');
              }

              const data = await response.json();
              
              if (data?.features?.[0]) {
                const [lon, lat] = data.features[0].center;
                const confidence = data.features[0].relevance;

                // Add warning for low confidence matches
                if (confidence < 0.8) {
                  newGeocodingErrors[lead._id] = 'Low confidence address match';
                }

                return {
                  ...lead,
                  coordinates: [lat, lon],
                  formattedAddress: data.features[0].place_name,
                  geocodingConfidence: confidence
                };
              }
              
              newGeocodingErrors[lead._id] = 'Could not find coordinates for this address';
              return {
                ...lead,
                coordinates: null,
                formattedAddress: null,
                geocodingConfidence: 0
              };

            } catch (error) {
              console.error('Geocoding error for lead:', lead._id, error);
              newGeocodingErrors[lead._id] = 'Error geocoding address';
              return {
                ...lead,
                coordinates: null,
                formattedAddress: null,
                geocodingConfidence: 0
              };
            }
          })
        );

        setGeocodingErrors(newGeocodingErrors);
        setMapLeads(leadsWithCoordinates);
        setFilteredLeads(leadsWithCoordinates);
      } catch (error) {
        console.error('Geocoding process error:', error);
      } finally {
        setGeocodingLoading(false);
      }
    };

    if (leads && leads.length > 0) {
      getCoordinates();
    } else {
      setMapLeads([]);
      setFilteredLeads([]);
    }
  }, [leads]);

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
  };

  const handleViewInMap = (lead) => {
    setSelectedLead(lead);
  };

  return (
    <DashboardLayout>
      <DashboardHeader 
        hideCreateLead={true}
        hideActions={true}
        hideTabBar={true}
        hideSearch={true}
      />
      
      <div className="p-2 md:p-4 h-[calc(100vh-120px)] flex flex-col">
        {apiLoading || geocodingLoading ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Spinner size="xl" color="blue" />
            <p className="mt-4 text-gray-600">
              {apiLoading ? 'Loading leads...' : 'Processing addresses...'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 h-full">
            {/* Map - Full width on mobile, flex-1 on desktop */}
            <div className="h-[40vh] md:h-full md:flex-1 order-2 md:order-1">
              <div className="bg-white rounded-lg shadow-lg h-full">
                <MapComponent 
                  mapLeads={mapLeads} // Show all leads on map
                  handleLeadClick={handleLeadClick}
                  selectedLead={selectedLead}
                />
              </div>
            </div>

            {/* Lead Cards - Full width on mobile, fixed width on desktop */}
            <div className="w-full md:w-96 order-1 md:order-2 flex flex-col h-[60vh] md:h-full">
              {/* Search and filter section */}
              <div className="bg-white mb-2 p-3 rounded-lg shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <span className="text-sm text-gray-500">Results per page:</span>
                    <select 
                      value={limit} 
                      onChange={handleLimitChange}
                      className="ml-2 text-sm border rounded p-1"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                  <div className="text-sm text-gray-500">
                    {filteredLeads.length} leads found
                  </div>
                </div>

                {/* Search bar */}
                <div className="relative mb-3">
                  <input
                    type="text"
                    placeholder="Search leads..."
                    value={searchTerm}
                    onChange={(e) => handleClientSearch(e.target.value)}
                    className="w-full p-2 pl-10 pr-4 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <FaSearch className="absolute left-3 top-3 text-gray-400" />
                </div>

                {/* Priority filter */}
                <div className="flex items-center">
                  <FaFilter className="text-gray-400 mr-2" />
                  <span className="text-sm text-gray-500 mr-2">Priority:</span>
                  <select
                    value={priority}
                    onChange={handlePriorityChange}
                    className="text-sm border rounded p-1 flex-1"
                  >
                    <option value="all">All Priorities</option>
                    <option value="high">High Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="low">Low Priority</option>
                  </select>
                </div>
              </div>

              {/* Scrollable area for lead cards */}
              <div className="flex-1 overflow-y-auto mb-2 custom-scrollbar">
                <div className="space-y-2 md:space-y-3 pr-2">
                  {filteredLeads.length === 0 ? (
                    <div className="text-center p-4 md:p-8 bg-white rounded-lg shadow">
                      <div className="text-gray-400 mb-3">
                        <FaMapMarkerAlt className="w-8 md:w-12 h-8 md:h-12 mx-auto" />
                      </div>
                      <p className="text-gray-500 text-base md:text-lg">
                        No leads found matching filters
                      </p>
                    </div>
                  ) : (
                    paginatedLeads.map((lead) => (
                      <LeadCard
                        key={lead._id}
                        lead={lead}
                        isSelected={selectedLead?._id === lead._id}
                        onClick={() => handleLeadClick(lead)}
                        onViewInMap={() => handleViewInMap(lead)}
                        error={geocodingErrors[lead._id]}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Pagination controls */}
              {totalPages > 1 && (
                <div className="mt-auto flex items-center justify-between bg-white p-3 rounded-lg shadow-sm">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={`flex items-center space-x-1 text-sm 
                      ${currentPage === 1 
                        ? 'text-gray-300 cursor-not-allowed' 
                        : 'text-blue-600 hover:text-blue-800'}`}
                  >
                    <FaChevronLeft className="w-3 h-3" />
                    <span>Previous</span>
                  </button>
                  
                  <div className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </div>
                  
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage >= totalPages}
                    className={`flex items-center space-x-1 text-sm 
                      ${currentPage >= totalPages 
                        ? 'text-gray-300 cursor-not-allowed' 
                        : 'text-blue-600 hover:text-blue-800'}`}
                  >
                    <span>Next</span>
                    <FaChevronRight className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MapView; 